import React, { useState } from 'react';
import apiService from '../../services/api';
import logger from '../../services/logger';
import { useTranslation } from 'react-i18next';

export const AccountTab: React.FC = () => {
  const { t } = useTranslation();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);

    if (!oldPassword || !newPassword) {
      setError(t('admin.account.fillAllFields'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('admin.account.passwordsDontMatch'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('admin.account.minLength'));
      return;
    }

    setLoading(true);
    try {
      logger.userAction('change_password_attempt');
      await apiService.changePassword(oldPassword, newPassword);
      setSuccess(t('admin.account.changeSuccess'));
      logger.userAction('change_password_success');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.detail || err?.message || t('admin.account.changeFailed');
      setError(msg);
      logger.error('ACCOUNT', 'change_password_failed', { error: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tc-card p-6 max-w-xl">
      <h3 className="text-xl font-semibold mb-4 tc-text-primary">{t('admin.account.title')}</h3>
      <p className="tc-text-secondary mb-6">{t('admin.account.subtitle')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="oldPassword" className="block text-sm font-medium tc-text-secondary mb-1">{t('admin.account.currentPassword')}</label>
          <input
            id="oldPassword"
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            className="tc-input w-full"
            required
            autoComplete="current-password"
          />
        </div>

        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium tc-text-secondary mb-1">{t('admin.account.newPassword')}</label>
          <input
            id="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="tc-input w-full"
            required
            autoComplete="new-password"
            minLength={8}
          />
          <p className="text-xs tc-text-tertiary mt-1">{t('admin.account.passwordHint')}</p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium tc-text-secondary mb-1">{t('admin.account.confirmNewPassword')}</label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="tc-input w-full"
            required
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        {error && (
          <div className="error-bg rounded-md p-3">
            <p className="text-sm">{error}</p>
          </div>
        )}
        {success && (
          <div className="success-bg rounded-md p-3">
            <p className="text-sm">{success}</p>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button type="submit" className="tc-btn tc-btn-primary" disabled={loading}>
            {loading ? t('admin.account.updating') : t('admin.account.changePassword')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountTab;
